import {
    DAY_IDS,
    DAY_TEXT_LABELS,
    MESSAGE_COLOR,
    OVERLAP_PALETTE,
    OVERLAP_PALETTE_INSERTS,
    SCHEDULE_TYPE,
    SILENCE_COLOR,
} from './constants';
import type { ScheduleKind, ScheduleRow, ScheduleState } from './types';
import {
    combineWallClock,
    decodeDayIds,
    nextCalendarDate,
    normalizeCalendarDate,
    parseCalendarDate,
    parseClockTime,
    positiveInteger,
    timesCrossMidnight,
    toBool,
} from './values';

/**
 * Written after a time window that ends the next day, as in "10:00 PM–6:00 AM
 * next day". Spelled out: a bare "+1" was not clear enough to read.
 */
export const NEXT_DAY = 'next day';

export const SCHEDULE_KIND_LABELS: Readonly<Record<ScheduleKind, string>> = {
    oneoff: 'One-off',
    recurring: 'Recurring',
    insert: 'Insert',
    message: 'Message',
    silence: 'Silence',
    stationchange: 'Station change',
    unknown: 'Unknown',
};

/** Silence is a MESSAGING row with the silence flag; the flag means nothing on other types. */
export function scheduleKind(row: ScheduleRow): ScheduleKind {
    switch (row.custom_playlist_schedule_type_id) {
        case SCHEDULE_TYPE.DATETIME:
            return 'oneoff';
        case SCHEDULE_TYPE.RECURRING:
            return 'recurring';
        case SCHEDULE_TYPE.STATION_CHANGE:
            return 'stationchange';
        case SCHEDULE_TYPE.INSERT:
            return 'insert';
        case SCHEDULE_TYPE.MESSAGING:
            return toBool(row.silence) ? 'silence' : 'message';
        default:
            return 'unknown';
    }
}

/**
 * Whether the players treat the row as weekly. Type 2 always is. Inserts and
 * messages have no column for it: the players decide by whether
 * `delimited_day_ids` is non-empty (physicalplayer common.php:6860, 6912, 6935).
 */
export function isRecurringRow(row: ScheduleRow): boolean {
    const typeId = row.custom_playlist_schedule_type_id;
    if (typeId === SCHEDULE_TYPE.RECURRING) {
        return true;
    }
    if (typeId === SCHEDULE_TYPE.INSERT || typeId === SCHEDULE_TYPE.MESSAGING) {
        return decodeDayIds(row.delimited_day_ids).length > 0;
    }
    return false;
}

function usesMessagingTimes(kind: ScheduleKind): boolean {
    return kind === 'message' || kind === 'silence';
}

/** The daily window the players use: messaging_* times for messages and silence, else time/end_time. */
export function scheduleTimes(row: ScheduleRow): { start: string | null; end: string | null } {
    if (usesMessagingTimes(scheduleKind(row))) {
        return { start: row.messaging_start_time ?? null, end: row.messaging_end_time ?? null };
    }
    return { start: row.time ?? null, end: row.end_time ?? null };
}

/** "1970-01-01" is the trigger's fallback for a missing start date, not a real date. */
function realCalendarDate(value: string | null | undefined): string | null {
    const normalized = normalizeCalendarDate(value);
    return normalized === '1970-01-01' ? null : normalized;
}

/** The last second of a calendar day, for an end date that has no end time. */
function endOfDay(date: string): Date | null {
    return combineWallClock(date, '23:59:59');
}

/**
 * The row's overall window, read the way the PLAYERS read it (amendment A5),
 * not from the trigger's `effective_*` columns. The two disagree: the trigger
 * decides whether an insert or message repeats by its recurring dates, while the
 * players decide by its day ids.
 *
 * - A repeating row (see isRecurringRow) runs from `recurring_start_date` to
 *   `recurring_end_date`. When a type-2 row has no usable repeat interval,
 *   getlists.php:364-368 strips both dates, so the window is open at both ends.
 *   It ends at the end time ON the end date, with no next-day shift, even for a
 *   window that crosses midnight: gvplayer expires it once now is past
 *   makestamp(end_time, recurring_end_date) (GVPlaylists.cpp:1902-1906, and
 *   2603-2606 / 2911-2914 for inserts and messages). So the end date's own
 *   night never plays.
 * - A dated row runs from `date` to `end_date`.
 * - Both use the daily times from scheduleTimes (messaging times for messages
 *   and silence). A missing start time is midnight; an end date without an end
 *   time lasts the whole day. A missing end date is open-ended.
 */
export function scheduleWindow(row: ScheduleRow): { start: Date | null; end: Date | null } {
    const times = scheduleTimes(row);
    if (isRecurringRow(row)) {
        const datesStripped =
            row.custom_playlist_schedule_type_id === SCHEDULE_TYPE.RECURRING &&
            positiveInteger(row.recurring_weekly_interval) === null;
        if (datesStripped) {
            return { start: null, end: null };
        }
        const startDate = realCalendarDate(row.recurring_start_date);
        const endDate = normalizeCalendarDate(row.recurring_end_date);
        let end: Date | null = null;
        if (endDate) {
            end = times.end ? combineWallClock(endDate, times.end) : endOfDay(endDate);
        }
        return { start: startDate ? combineWallClock(startDate, times.start) : null, end };
    }
    const startDate = realCalendarDate(row.date);
    const endDate = normalizeCalendarDate(row.end_date);
    let end: Date | null = null;
    if (endDate) {
        end = times.end ? combineWallClock(endDate, times.end) : endOfDay(endDate);
    }
    return { start: startDate ? combineWallClock(startDate, times.start) : null, end };
}

/**
 * Expired once `now` is past the end; upcoming before the start; otherwise in
 * force. A null end never expires.
 *
 * A row the players never play at all (see {@link NEVER_PLAYS}) reads as
 * expired whatever its dates say. It is not scheduled, so it must not sit under
 * Active or be the schedule a card shows. Its warning line says why. The count
 * boxes still count it, as they count every schedule.
 */
export function scheduleState(row: ScheduleRow, now: Date): ScheduleState {
    if (scheduleProblems(row).some((problem) => NEVER_PLAYS.has(problem))) {
        return 'expired';
    }
    const { start, end } = scheduleWindow(row);
    const nowMs = now.getTime();
    if (end && nowMs > end.getTime()) {
        return 'expired';
    }
    if (start && nowMs < start.getTime()) {
        return 'upcoming';
    }
    return 'in-force';
}

export const SCHEDULE_PROBLEMS = {
    RECURRING_WITHOUT_DAYS:
        'No days are selected. The server drops a recurring schedule with no days, so it never plays.',
    RECURRING_WITHOUT_INTERVAL:
        'The repeat interval is empty. The server then ignores the start and end dates, so it plays every week with no end.',
    RECURRING_INSERT_WITHOUT_START:
        'This repeating insert has no start date. The player never starts it.',
    MESSAGE_WITHOUT_START_TIME:
        'This message has no messaging start time. The player skips it.',
    SILENCE_WITHOUT_START_TIME:
        'This silence has no messaging start time. The player skips it.',
    MESSAGE_WITHOUT_FREQUENCY:
        'This message has no repeat interval in minutes. The player skips it.',
    RECURRING_INSERT_WITHOUT_END_TIME:
        'This repeating insert has no end time. The player ignores it.',
    INTERVAL_WITHOUT_START:
        'It repeats every few weeks but has no start date to count from, so the player plays it every week.',
    DATED_WITHOUT_DATE: 'This dated insert or message has no date, so it never plays.',
    REPEAT_DATES_WITHOUT_DAYS:
        'It has repeat dates but no days, so the player treats it as a one-off.',
} as const;

/**
 * The problems that mean the players never play the row at all. The other
 * problems change how a row plays (every week instead of every other week, a
 * one-off instead of a repeat) but it still plays.
 */
const NEVER_PLAYS: ReadonlySet<string> = new Set([
    SCHEDULE_PROBLEMS.RECURRING_WITHOUT_DAYS,
    SCHEDULE_PROBLEMS.RECURRING_INSERT_WITHOUT_START,
    SCHEDULE_PROBLEMS.RECURRING_INSERT_WITHOUT_END_TIME,
    SCHEDULE_PROBLEMS.MESSAGE_WITHOUT_START_TIME,
    SCHEDULE_PROBLEMS.SILENCE_WITHOUT_START_TIME,
    SCHEDULE_PROBLEMS.MESSAGE_WITHOUT_FREQUENCY,
    SCHEDULE_PROBLEMS.DATED_WITHOUT_DATE,
]);

/**
 * The silent failure modes: rows the server accepts and passes on (or drops
 * without a word) that never play as written.
 */
export function scheduleProblems(row: ScheduleRow): string[] {
    const problems: string[] = [];
    const typeId = row.custom_playlist_schedule_type_id;
    const dayIds = decodeDayIds(row.delimited_day_ids);

    if (typeId === SCHEDULE_TYPE.RECURRING) {
        // getlists.php:323-325 skips a type-2 row whose day_ids is empty.
        if (dayIds.length === 0) {
            problems.push(SCHEDULE_PROBLEMS.RECURRING_WITHOUT_DAYS);
        }
        // getlists.php:364-368 strips recurring_start_date and recurring_end_date
        // whenever the interval is missing or <= 0. Only a loss when there are dates.
        const hasDates = Boolean(row.recurring_start_date || row.recurring_end_date);
        const interval = positiveInteger(row.recurring_weekly_interval);
        if (hasDates && interval === null) {
            problems.push(SCHEDULE_PROBLEMS.RECURRING_WITHOUT_INTERVAL);
        }
        // The players run the weekly-interval test only when a start date is set
        // (gvplayer GVPlaylists.cpp:1919-1948, physicalplayer common.php:5719-5741).
        if (interval !== null && interval > 1 && !realCalendarDate(row.recurring_start_date)) {
            problems.push(SCHEDULE_PROBLEMS.INTERVAL_WITHOUT_START);
        }
    }

    // gvplayer GVPlaylists.cpp:2618 only activates a recurring insert when
    // recurring_start_date is set.
    if (typeId === SCHEDULE_TYPE.INSERT && dayIds.length > 0 && !row.recurring_start_date) {
        problems.push(SCHEDULE_PROBLEMS.RECURRING_INSERT_WITHOUT_START);
    }
    // gvplayer GVPlaylists.cpp:2572 and 2593-2596 skip a recurring insert with no end_time.
    if (typeId === SCHEDULE_TYPE.INSERT && dayIds.length > 0 && !row.end_time) {
        problems.push(SCHEDULE_PROBLEMS.RECURRING_INSERT_WITHOUT_END_TIME);
    }

    // gvplayer GVPlaylists.cpp:2703-2759 checks messaging_start_time before it
    // looks at the silence flag, so it skips a message OR a silence without it.
    // Past the silence branch it also skips a message with no frequency.
    if (typeId === SCHEDULE_TYPE.MESSAGING) {
        const silence = toBool(row.silence);
        if (!row.messaging_start_time) {
            problems.push(silence ? SCHEDULE_PROBLEMS.SILENCE_WITHOUT_START_TIME : SCHEDULE_PROBLEMS.MESSAGE_WITHOUT_START_TIME);
        }
        if (!silence && positiveInteger(row.messaging_frequency_minutes) === null) {
            problems.push(SCHEDULE_PROBLEMS.MESSAGE_WITHOUT_FREQUENCY);
        }
    }

    // The players treat an insert or message with no day ids as dated on `date`
    // (physicalplayer common.php:6860, 6912, 6935), whatever its recurring dates say.
    if ((typeId === SCHEDULE_TYPE.INSERT || typeId === SCHEDULE_TYPE.MESSAGING) && dayIds.length === 0) {
        if (!normalizeCalendarDate(row.date)) {
            problems.push(SCHEDULE_PROBLEMS.DATED_WITHOUT_DATE);
        }
        if (row.recurring_start_date || row.recurring_end_date) {
            problems.push(SCHEDULE_PROBLEMS.REPEAT_DATES_WITHOUT_DAYS);
        }
    }

    return problems;
}

const MONTH_ABBREVIATIONS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

function meridiemParts(time: string): { clock: string; meridiem: 'am' | 'pm' } | null {
    const parsed = parseClockTime(time);
    if (!parsed) {
        return null;
    }
    const meridiem = parsed.hours < 12 ? 'am' : 'pm';
    const hour12 = parsed.hours % 12 === 0 ? 12 : parsed.hours % 12;
    // The short form tabasco-react's Day view uses ("8am"), with the minutes
    // kept when they are not :00 ("1:30am"). React drops them (SAU-389).
    const clock = parsed.minutes === 0 ? String(hour12) : `${hour12}:${String(parsed.minutes).padStart(2, '0')}`;
    return { clock, meridiem };
}

/** "6am" for "06:00:00", "1:30pm" for "13:30:00". Seconds are dropped. */
export function formatClockTime(time: string | null | undefined): string | null {
    if (!time) {
        return null;
    }
    const parts = meridiemParts(time);
    return parts ? `${parts.clock}${parts.meridiem}` : null;
}

/**
 * "8am-6pm", "1:30am-2:30am", "10pm-6am": tabasco-react's short form. Both ends
 * always carry am or pm, as React's do, so a window never reads ambiguously.
 * An open end reads "from 6am" or "until 2pm".
 */
export function formatTimeRange(
    startTime: string | null | undefined,
    endTime: string | null | undefined,
): string | null {
    const start = formatClockTime(startTime);
    const end = formatClockTime(endTime);
    if (start && end) {
        return `${start}-${end}`;
    }
    if (start) {
        return `from ${start}`;
    }
    if (end) {
        return `until ${end}`;
    }
    return null;
}

/** "Sun 13 Sep 2026". Every date carries its year, the current year included. */
export function formatScheduleDay(value: string | null | undefined): string | null {
    const parsed = parseCalendarDate(value);
    if (!parsed) {
        return null;
    }
    const weekday = new Date(parsed.year, parsed.month - 1, parsed.day).getDay();
    return `${DAY_IDS[weekday]!.abbr} ${parsed.day} ${MONTH_ABBREVIATIONS[parsed.month - 1]} ${parsed.year}`;
}

/** "13 Sep 2026". Every date carries its year, the current year included. */
export function formatScheduleShortDate(value: string | null | undefined): string | null {
    const parsed = parseCalendarDate(value);
    if (!parsed) {
        return null;
    }
    return `${parsed.day} ${MONTH_ABBREVIATIONS[parsed.month - 1]} ${parsed.year}`;
}

/**
 * An insert whose frequency moves from one track count to another over its
 * ramp days. The direction is how often the insert plays, not which way the
 * number goes: every 8 tracks to every 3 plays more often, so it ramps up.
 */
export interface InsertRamp {
    fromTracks: number;
    toTracks: number;
    direction: 'up' | 'down';
}

/** The plain-text stand-ins for the trend arrow the cards draw between the two counts. */
export const RAMP_GLYPHS: Readonly<Record<InsertRamp['direction'], string>> = { up: '↗', down: '↘' };

/** One piece of the qualifier. A ramp piece carries its counts so a card can draw the arrow as an icon. */
export interface QualifierPart {
    text: string;
    ramp?: InsertRamp;
}

export interface ScheduleSummary {
    kind: ScheduleKind;
    /** 'daily' when all seven days; the day ids when partial (empty when none); null for a dated row. */
    days: number[] | 'daily' | null;
    timeRange: string | null;
    crossesMidnight: boolean;
    dateWindow: string | null;
    /** The qualifier in its pieces: "every 2 wks", "every 8 ↗ 3 tracks", "ramp 7d". Empty when there is none. */
    qualifierParts: QualifierPart[];
    state: ScheduleState;
    problems: string[];
}

function datedWindow(row: ScheduleRow, crossesMidnight: boolean): string | null {
    const startDate = normalizeCalendarDate(row.date);
    const endDate = normalizeCalendarDate(row.end_date);
    const start = formatScheduleDay(startDate);
    const end = formatScheduleDay(endDate);
    if (!start) {
        return end ? `until ${end}` : null;
    }
    if (!endDate || !end) {
        return `from ${start}`;
    }
    if (endDate === startDate) {
        return start;
    }
    // A window that ends the next morning reads as one day plus the midnight marker.
    if (crossesMidnight && nextCalendarDate(startDate!) === endDate) {
        return start;
    }
    return `${start} – ${end}`;
}

/**
 * Whether the row's times are a window the player opens every day. True for
 * repeating rows, and for messages and silence even on dates (gvplayer checks
 * the messaging window each day, GVPlaylists.cpp:2720-2735). False for a dated
 * one-off or insert: those run as ONE continuous span from date + time to
 * end_date + end_time (physicalplayer common.php:7561-7577).
 */
function timesAreDailyWindow(kind: ScheduleKind, recurring: boolean): boolean {
    return recurring || usesMessagingTimes(kind);
}

/**
 * The date and time parts of a dated one-off or insert. A span that ends on the
 * next day keeps the "next day" reading. A longer span puts each time next to its
 * own date ("Tue 22 Sep 2026 9:00 AM – Thu 24 Sep 2026 5:00 PM"), because "9:00 AM–5:00 PM"
 * after a date range reads as a daily window, which is not how it plays.
 */
function continuousSpan(
    row: ScheduleRow,
    times: { start: string | null; end: string | null },
): { dateWindow: string | null; timeRange: string | null; crossesMidnight: boolean } {
    const startDate = normalizeCalendarDate(row.date);
    const endDate = normalizeCalendarDate(row.end_date);
    const endsNextDay = Boolean(startDate && endDate && times.start && times.end && nextCalendarDate(startDate) === endDate);
    const dateWindow = datedWindow(row, endsNextDay);
    const timeRange = formatTimeRange(times.start, times.end);
    const multiDay = Boolean(startDate && endDate && endDate > startDate && !endsNextDay);
    if (!multiDay || (!times.start && !times.end)) {
        return { dateWindow, timeRange, crossesMidnight: endsNextDay };
    }
    const startText = [formatScheduleDay(startDate), formatClockTime(times.start)].filter(Boolean).join(' ');
    const endText = [formatScheduleDay(endDate), formatClockTime(times.end)].filter(Boolean).join(' ');
    return { dateWindow: `${startText} – ${endText}`, timeRange: null, crossesMidnight: false };
}

function recurringWindow(row: ScheduleRow, state: ScheduleState): string | null {
    // The trigger's fallback start date is not a real start.
    const rawStart = normalizeCalendarDate(row.recurring_start_date);
    const start = rawStart === '1970-01-01' ? null : formatScheduleShortDate(rawStart);
    const end = formatScheduleShortDate(row.recurring_end_date);
    if (state === 'in-force') {
        // Already running: only the end is news.
        return end ? `to ${end}` : null;
    }
    if (start && end) {
        return `${start} – ${end}`;
    }
    if (start) {
        return `from ${start}`;
    }
    return end ? `to ${end}` : null;
}

/**
 * The words either side of a ramp's arrow: "every 8" and "3 tracks", or
 * "1 track" when it ends at one. The text form and the card both build the
 * ramp from these, so the two cannot drift apart.
 */
export function insertRampWords(ramp: InsertRamp): { before: string; after: string } {
    return { before: `every ${ramp.fromTracks}`, after: `${ramp.toTracks} ${ramp.toTracks === 1 ? 'track' : 'tracks'}` };
}

/** "every 8 ↗ 3 tracks": fewer tracks between inserts means it plays more often. */
export function insertRampText(ramp: InsertRamp): string {
    const words = insertRampWords(ramp);
    return `${words.before} ${RAMP_GLYPHS[ramp.direction]} ${words.after}`;
}

function insertQualifier(row: ScheduleRow): QualifierPart[] {
    const parts: QualifierPart[] = [];
    const fromTracks = positiveInteger(row.insert_frequency_tracks_start);
    const toTracks = positiveInteger(row.insert_frequency_tracks_target);
    if (fromTracks !== null && toTracks !== null && fromTracks !== toTracks) {
        const ramp: InsertRamp = { fromTracks, toTracks, direction: toTracks < fromTracks ? 'up' : 'down' };
        parts.push({ text: insertRampText(ramp), ramp });
    } else if (fromTracks !== null || toTracks !== null) {
        const tracks = fromTracks ?? toTracks;
        parts.push({ text: tracks === 1 ? 'every track' : `every ${tracks} tracks` });
    }
    const rampDays = positiveInteger(row.insert_frequency_ramp_days);
    if (rampDays !== null) {
        parts.push({ text: `ramp ${rampDays}d` });
    }
    return parts;
}

export interface SummarizeOptions {
    /** The owner zone's wall-clock time (amendment A6). */
    now: Date;
    /**
     * The name of a location_zone_players row, for a station change. The caller
     * owns the lookup; without an answer the row reads "→ player #2007".
     */
    playerName?: (locationZonePlayerId: number) => string | null | undefined;
}

/** "→ Main Street-Bar" once the player's name is known, else "→ player #2007". */
export function stationChangeTarget(
    locationZonePlayerId: number | null | undefined,
    playerName?: SummarizeOptions['playerName'],
): string | null {
    const id = positiveInteger(locationZonePlayerId);
    if (id === null) return null;
    const name = playerName?.(id);
    return name ? `→ ${name}` : `→ player #${id}`;
}

export function summarizeSchedule(row: ScheduleRow, { now, playerName }: SummarizeOptions): ScheduleSummary {
    const kind = scheduleKind(row);
    const recurring = isRecurringRow(row);
    const times = scheduleTimes(row);
    const state = scheduleState(row, now);

    let dateWindow: string | null;
    let timeRange: string | null;
    let crossesMidnight: boolean;
    if (timesAreDailyWindow(kind, recurring)) {
        crossesMidnight = timesCrossMidnight(times.start, times.end);
        timeRange = formatTimeRange(times.start, times.end);
        dateWindow = recurring ? recurringWindow(row, state) : datedWindow(row, crossesMidnight);
    } else {
        ({ dateWindow, timeRange, crossesMidnight } = continuousSpan(row, times));
    }
    // "from Sun 13 Sep 2026 8:00 AM", not "from Sun 13 Sep 2026 · from 8:00 AM".
    if (!recurring && dateWindow?.startsWith('from ') && timeRange?.startsWith('from ')) {
        dateWindow = `${dateWindow} ${timeRange.slice('from '.length)}`;
        timeRange = null;
    }

    let days: ScheduleSummary['days'] = null;
    if (recurring) {
        const dayIds = decodeDayIds(row.delimited_day_ids);
        days = dayIds.length === 7 ? 'daily' : dayIds;
    }

    const qualifierParts: QualifierPart[] = [];
    const interval = positiveInteger(row.recurring_weekly_interval);
    // Without a start date the players skip the interval test and play every week.
    if (recurring && interval !== null && interval > 1 && realCalendarDate(row.recurring_start_date)) {
        qualifierParts.push({ text: `every ${interval} wks` });
    }
    if (kind === 'insert') {
        qualifierParts.push(...insertQualifier(row));
    }
    if (kind === 'message') {
        const minutes = positiveInteger(row.messaging_frequency_minutes);
        if (minutes !== null) {
            qualifierParts.push({ text: `every ${minutes} min` });
        }
    }
    if (kind === 'stationchange') {
        // station_change_location_zone_player_id is a location_zone_players id, not a player number.
        const target = stationChangeTarget(row.station_change_location_zone_player_id, playerName);
        if (target) qualifierParts.push({ text: target });
    }

    return {
        kind,
        days,
        timeRange,
        crossesMidnight,
        dateWindow,
        qualifierParts,
        state,
        problems: scheduleProblems(row),
    };
}

const KIND_TEXT_PREFIX: Readonly<Record<ScheduleKind, string>> = {
    oneoff: '',
    recurring: '↻',
    insert: 'Insert',
    message: 'Message',
    silence: 'Silence',
    stationchange: '⇄',
    unknown: '',
};

/** The qualifier as one line of text: "every 3 wks · every 4 tracks". Null when there is none. */
export function qualifierText(summary: ScheduleSummary): string | null {
    return summary.qualifierParts.length > 0 ? summary.qualifierParts.map((part) => part.text).join(' · ') : null;
}

/**
 * A repeating schedule's date window: "to 22 Nov 2026", "1 Oct 2026 – 2 Nov 2026", or "no end
 * date" when it has none. Null for a dated schedule, whose dates already say
 * when it runs. The card's first line and the drawer line both use this, so
 * they agree about an open-ended schedule.
 */
export function repeatWindowText(summary: ScheduleSummary): string | null {
    if (summary.days === null) return null;
    return summary.dateWindow ?? 'no end date';
}

/**
 * The icons that lead the detail line. A playlist's own type already says
 * whether it plays once or repeats, so it gets that one icon. Every other type
 * gets its type icon, then the one-off or recurring icon.
 */
export function scheduleDetailIcons(summary: ScheduleSummary): { type: ScheduleKind | null; cadence: 'oneoff' | 'recurring' | null } {
    if (summary.kind === 'oneoff' || summary.kind === 'recurring') {
        return { type: null, cadence: summary.kind };
    }
    if (summary.kind === 'unknown') {
        return { type: 'unknown', cadence: null };
    }
    return { type: summary.kind, cadence: summary.days !== null ? 'recurring' : 'oneoff' };
}

/** "Every day", "No days", or each day's label in the order given. The summary's days are already Sunday first. */
function formatDayList(days: number[] | 'daily', label: (dayId: number) => string | undefined): string {
    if (days === 'daily') {
        return 'Every day';
    }
    if (days.length === 0) {
        return 'No days';
    }
    return days.map(label).join(' ');
}

/** "M W F", "Every day", or "No days". */
export function formatDays(days: number[] | 'daily'): string {
    return formatDayList(days, (dayId) => DAY_TEXT_LABELS[dayId]);
}

/** "Mon Wed Fri", "Every day", or "No days": the days as a schedule card writes them. */
export function formatDayNames(days: number[] | 'daily'): string {
    return formatDayList(days, (dayId) => DAY_IDS[dayId - 1]?.abbr);
}

/**
 * One line of plain text for a tooltip or a joined cell:
 * "↻ M W F 1:30–2:30 AM", "↻ Every day 6am-11pm", "Sun 13 Sep 2026 1:30–2:30 AM".
 * A window that ends the next day says so: "10:00 PM–6:00 AM next day".
 * The schedule card passes formatDayNames, so its tooltip spells the days the
 * way its first line does.
 */
export function summaryText(summary: ScheduleSummary, { dayLabels = formatDays }: { dayLabels?: (days: number[] | 'daily') => string } = {}): string {
    const when = summary.days !== null ? dayLabels(summary.days) : summary.dateWindow;
    let time = summary.timeRange ? `${summary.timeRange}${summary.crossesMidnight ? ` ${NEXT_DAY}` : ''}` : null;
    // "from Sun 13 Sep 2026 6:00 AM", not "from Sun 13 Sep 2026 from 6:00 AM".
    if (time && when?.startsWith('from ') && time.startsWith('from ')) {
        time = time.slice('from '.length);
    }
    const head = [KIND_TEXT_PREFIX[summary.kind], when, time].filter(Boolean).join(' ');
    // The plain text stays terse: an open-ended schedule adds nothing, where the card says "no end date".
    const tail = [qualifierText(summary), summary.days !== null ? summary.dateWindow : null].filter(Boolean);
    return [head, ...tail].join(' · ');
}

/**
 * tabasco-react processEventsWithColors, exactly: silence and message have
 * fixed colours; inserts and everything else index their palette by priority,
 * falling back to 100 / 10 when priority is empty — so with the null priority
 * that production rows carry, every insert is #76D6FF and every other row #521B93.
 */
export function scheduleColor(row: ScheduleRow): string {
    return scheduleKindColor(scheduleKind(row), row.priority);
}

/**
 * The colour for a schedule type, before any priority: what a new schedule of
 * that type is drawn in, and what the editor's type menu shows beside each type.
 */
export function scheduleKindColor(kind: ScheduleKind, priority?: number | null): string {
    if (kind === 'silence') {
        return SILENCE_COLOR;
    }
    if (kind === 'message') {
        return MESSAGE_COLOR;
    }
    if (kind === 'insert') {
        const insertPriority = priority || 100;
        return OVERLAP_PALETTE_INSERTS[Math.max(0, insertPriority - 100) % OVERLAP_PALETTE_INSERTS.length]!;
    }
    const normalPriority = priority || 10;
    return OVERLAP_PALETTE[Math.max(0, normalPriority - 10) % OVERLAP_PALETTE.length]!;
}
